/**
 * Tester Management
 *
 * Firestore-based allowlist for lab access
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export interface Tester {
  email: string;
  displayName?: string;
  addedAt: Date;
  addedBy: string;
  active: boolean;
  notes?: string;
}

const TESTERS_COLLECTION = 'lab_testers';

/**
 * Check if an email has tester access
 */
export async function checkTesterAccess(email: string): Promise<boolean> {
  try {
    const testerRef = doc(db, TESTERS_COLLECTION, email);
    const testerSnap = await getDoc(testerRef);

    if (!testerSnap.exists()) return false;

    const tester = testerSnap.data() as Tester;
    return tester.active === true;
  } catch (error) {
    console.error('Error checking tester access:', error);
    return false;
  }
}

/**
 * Get all testers
 */
export async function getAllTesters(): Promise<Tester[]> {
  try {
    const testersRef = collection(db, TESTERS_COLLECTION);
    const snapshot = await getDocs(testersRef);

    return snapshot.docs.map((doc) => ({
      ...doc.data(),
      addedAt: doc.data().addedAt?.toDate() || new Date(),
    })) as Tester[];
  } catch (error) {
    console.error('Error getting testers:', error);
    return [];
  }
}

/**
 * Add a new tester
 */
export async function addTester(
  email: string,
  addedBy: string,
  displayName?: string,
  notes?: string
): Promise<void> {
  try {
    const testerRef = doc(db, TESTERS_COLLECTION, email);
    await setDoc(testerRef, {
      email,
      displayName: displayName || null,
      addedAt: serverTimestamp(),
      addedBy,
      active: true,
      notes: notes || null,
    });
  } catch (error) {
    console.error('Error adding tester:', error);
    throw error;
  }
}

/**
 * Update tester active status
 */
export async function updateTesterStatus(
  email: string,
  active: boolean
): Promise<void> {
  try {
    const testerRef = doc(db, TESTERS_COLLECTION, email);
    await setDoc(testerRef, { active }, { merge: true });
  } catch (error) {
    console.error('Error updating tester status:', error);
    throw error;
  }
}

/**
 * Remove a tester
 */
export async function removeTester(email: string): Promise<void> {
  try {
    const testerRef = doc(db, TESTERS_COLLECTION, email);
    await deleteDoc(testerRef);
  } catch (error) {
    console.error('Error removing tester:', error);
    throw error;
  }
}
